import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import {
  capitalizeFirstLetter,
  convertEpochToDateString,
  formatDateTimeToDate
} from '../../../../../utils/commonFunctions';

type ReturnedType = any;
interface Truck {
  truckNumber: string;
  truckMake: string;
  truckEngineNumber: string;
  truckRegistrationDate: number;
  truckExpirationDate: number;
  truckChassisNumber: string;
  truckModel: string;
  truckColor: string;
  fuelCapacity: number;
  fuelType: string;
  overSpeedLimit: number;
  lastServicedDate: number;
  manufacturedYear: string;
  subType: string;
  emissionStandard: string;
  driveType: string;
  engineType: string;
  transmissionType: string;
  engineCapacity: string;
  truckUnladenWeight: number;
  truckUnladenWeightUnit: string;
  numberOfAxles: number;
  suspension: string;
  horsePower: number;
  brakeType: string;
  radioEquipment: string;
}

type initialStateProps = {
  isLoading: boolean;
  type: string;
  data: any;
  error: string | null;
  count: null;
  fileUrls: string[];
};

const initialState: initialStateProps = {
  isLoading: false,
  type: 'addTruck',
  data: [],
  error: null,
  count: null,
  fileUrls: []
};

const pagination = {
  pageNo: 1,
  pageSize: 5
};

// get truck
export const getTruck = createAsyncThunk<ReturnedType, any>(
  'api/getTruck',
  async (data, thunkApi) => {
    try {
      const {
        pageNo = 1,
        pageSize = 5,
        sortColumnName,
        sortBy,
        searchInput,
        searchColumnName,
        signal
      } = data;

      let url = `${URLs.GET_TRUCK}?pageNo=${pageNo}&pageSize=${pageSize}`;

      if (sortBy && sortColumnName && searchColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput && searchColumnName) {
        url += `&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput && searchColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput) {
        url += `&searchInput=${searchInput}`;
      } else if (sortBy && sortColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}`;
      }

      const response = await axiosInstance({
        method: 'GET',
        url: url,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message
              ? error?.response?.data?.message
              : error?.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// update truck
export const updateTruck = createAsyncThunk<ReturnedType, any>(
  'update/truck',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_TRUCK}`,
        data: data.updatePayload
      });
      await thunkApi.dispatch(getTruck(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      updateToast({
        show: true,
        message: error?.response?.data?.message
          ? error?.response?.data?.message
          : error?.message,
        severity: 'error'
      });
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get truck slice
const getTruckSlice = createSlice({
  name: 'getTruck',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTruck.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.type = 'getTrailer';
      })
      .addCase(getTruck.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        const {
          data: { truckDetails, truckDetailsListCount }
        } = action.payload;
        state.data = truckDetails?.map((item: any) => ({
          id: item?.id,
          vehicleNumber: item?.truckNumber,
          truckNumberCaps: (item?.truckNumber).toUpperCase(),
          unitNumber: item?.unitNumber,
          vehicleMake: item?.truckMake,
          truckMakeCaps: capitalizeFirstLetter(item?.truckMake),
          vehicleModel: item?.truckModel,
          truckModelCaps: capitalizeFirstLetter(item?.truckModel),
          fuelType: item?.fuelType,
          manufacturedYear: item?.manufacturedYear,
          unladenWeight: item?.truckUnladenWeight,
          numberOfAxles: item?.numberOfAxles,
          yearOfPurchase: item?.yearOfPurchase,
          height: item?.height,
          dimensionalUnits: item?.dimensionalUnits,
          grossVehicleWeight: item?.grossVehicleWeight,
          serialNumber: item?.serialNumber,
          dotInspectionDate: item?.dotInspectionDate,
          dotInspectionDateFormat: formatDateTimeToDate(
            convertEpochToDateString(item?.dotInspectionDate)
          ),
          insuranceDocumentNames: item?.insuranceModel?.documentNames,
          insuranceRegisterNumber: item?.insuranceModel?.registerNumber,

          insuranceRegisterNumberCaps: String(
            item?.insuranceModel?.registerNumber
          ).toUpperCase(),
          insuranceExpireDate: item?.insuranceModel?.expireDate,
          insuranceExpiry: formatDateTimeToDate(
            convertEpochToDateString(item?.insuranceModel?.expireDate)
          ),
          licenseDocumentNames: item?.licenseModel?.documentNames,
          licenseRegisterNumber: item?.licenseModel?.registerNumber,
          licenseRegisterNumberCaps: String(
            item?.licenseModel?.registerNumber
          ).toUpperCase(),
          licenseExpireDate: item?.licenseModel?.expireDate,
          licenseExpiry: formatDateTimeToDate(
            convertEpochToDateString(item?.licenseModel?.expireDate)
          ),
          fitnessDocumentNames: item?.fitnessModel?.documentNames,
          fitnessRegisterNumber: item?.fitnessModel?.registerNumber,
          fitnessRegisterNumberCaps: String(
            item?.fitnessModel?.registerNumber
          ).toUpperCase(),
          fitnessExpireDate: item?.fitnessModel?.expireDate,
          fitnessExpiry: formatDateTimeToDate(
            convertEpochToDateString(item?.fitnessModel?.expireDate)
          ),
          weightUnits: item?.weightUnits,
          type: 'Truck'
        }));
        state.count = truckDetailsListCount;
        state.error = null;
      })
      .addCase(getTruck.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// update truck slice
const updateTruckSlice = createSlice({
  name: 'updateTruck',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateTruck.pending, state => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(
      updateTruck.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      }
    );
    builder.addCase(updateTruck.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

//action for upload file
export const fileUpload = createAsyncThunk<any, any>(
  'fileuploadtruck',
  async (data, thunkApi) => {
    const {
      files: { url, file }
    } = data;
    try {
      const response = await axios({
        method: 'PUT',
        url: url,
        data: file
      });
      if (response) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'Truck added successfully',
            severity: 'success'
          })
        );
      }
      return response;
    } catch (error) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'Upload Failed',
          severity: 'error'
        })
      );
    }
  }
);

// slice for upload file
const fileUploadSlice = createSlice({
  name: 'fileUpload',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fileUpload.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fileUpload.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.fileUrls.push(action.payload?.fileUrl || '');
        state.error = null;
      })
      .addCase(fileUpload.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.message || 'Upload failed';
      });
  }
});

export const getTruckReducer = getTruckSlice.reducer;
export const updateTruckReducer = updateTruckSlice.reducer;
export const fileUploadReducer = fileUploadSlice.reducer;
